import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  FaDollarSign, FaShoppingBag, FaUsers, FaBoxOpen,
  FaArrowRight, FaExclamationTriangle,
} from 'react-icons/fa';
import { formatCurrency } from '../../utils/currency';
import { useSettings } from '../../context/SettingsContext';
import { LoadingState } from '../../components/Loading';

const STATUS_BADGE = {
  pending: 'warning',
  processing: 'info',
  shipped: 'info',
  delivered: 'success',
  cancelled: 'danger',
};

const Dashboard = () => {
  const { settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get('/api/dashboard/stats');
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <LoadingState />;

  const cards = [
    {
      key: 'revenue',
      label: 'Total revenue',
      value: formatCurrency(stats?.totalRevenue || 0, settings?.currency),
      icon: FaDollarSign,
      meta: 'From paid orders',
    },
    {
      key: 'orders',
      label: 'Total orders',
      value: (stats?.totalOrders || 0).toLocaleString(),
      icon: FaShoppingBag,
      meta: 'All time',
    },
    {
      key: 'customers',
      label: 'Customers',
      value: (stats?.totalCustomers || 0).toLocaleString(),
      icon: FaUsers,
      meta: 'Registered users',
    },
    {
      key: 'products',
      label: 'Products',
      value: (stats?.totalProducts || 0).toLocaleString(),
      icon: FaBoxOpen,
      meta: 'Active in catalog',
    },
  ];

  return (
    <div>
      <div className="admin-header">
        <div>
          <h1>Dashboard</h1>
          <p className="admin-header-subtitle">Welcome back. Here's what's happening today.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        {cards.map(({ key, label, value, icon: Icon, meta }) => (
          <div key={key} className={`stat-card ${key}`}>
            <div className="stat-card-header">
              <h3>{label}</h3>
              <span className="stat-icon"><Icon /></span>
            </div>
            <div className="stat-value">{value}</div>
            <div className="stat-meta">{meta}</div>
          </div>
        ))}
      </div>

      {/* Sales Chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2>Sales overview · Last 7 days</h2>
        </div>
        {stats?.salesByDay && stats.salesByDay.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={stats.salesByDay}>
              <defs>
                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5ea832" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#5ea832" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f3ed" />
              <XAxis dataKey="_id" stroke="#97a489" fontSize={12} />
              <YAxis stroke="#97a489" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e1e7da',
                  borderRadius: 8,
                  fontSize: 13,
                }}
                formatter={(value) => formatCurrency(value, settings?.currency)}
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#3f8a26"
                strokeWidth={2.5}
                fill="url(#salesGradient)"
                name="Sales"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--gray-500)' }}>
            No sales data for the last 7 days
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }} className="dashboard-bottom-grid">
        {/* Recent Orders */}
        <div className="card">
          <div className="card-header">
            <h2>Recent orders</h2>
            <Link to="/admin/orders" style={{ color: 'var(--primary-700)', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              View all <FaArrowRight />
            </Link>
          </div>
          {stats?.recentOrders && stats.recentOrders.length > 0 ? (
            <div className="admin-table-scroll" style={{ margin: '-8px -8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {stats.recentOrders.slice(0, 6).map((order) => (
                    <tr key={order._id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '12px 8px' }}>
                        <Link to={`/admin/orders/${order._id}`} style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
                          {order.orderNumber}
                        </Link>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                          {order.customer?.name}
                        </div>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span className={`badge ${STATUS_BADGE[order.status] || 'neutral'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 700 }}>
                        {formatCurrency(order.total, settings?.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--gray-500)', padding: '20px 0', textAlign: 'center' }}>
              No orders yet
            </p>
          )}
        </div>

        {/* Low Stock */}
        <div className="card">
          <div className="card-header">
            <h2>
              <FaExclamationTriangle style={{ color: 'var(--warning-500)', marginRight: 8 }} />
              Low stock alert
            </h2>
          </div>
          {stats?.lowStockProducts && stats.lowStockProducts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {stats.lowStockProducts.slice(0, 6).map((p) => (
                <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                  <span className={`badge ${p.stock === 0 ? 'danger' : 'warning'}`}>
                    {p.stock} left
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--gray-500)', padding: '20px 0', textAlign: 'center', fontSize: 13 }}>
              All products are well stocked
            </p>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .dashboard-bottom-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
